/**
 * ComfyUI 擦除/修复 API
 * 通过 Java 后端转发到 ComfyUI，使用 LaMa 模型擦除用户涂抹区域
 *
 * 流程：
 *   ① 上传原图 → imageUrl      （复用现有 uploadFile）
 *   ② 上传 mask → maskUrl       （黑底白字 PNG，白色 = 要擦除区域，尺寸同原图）
 *   ③ 提交：POST /web/v1/imageEditor  body: { imageUrl, maskUrl }
 *           ← 返回 { code:200, data:{ prompt_id } }
 *   ④ 轮询：GET  /web/v1/queryImageEditorResult?promptId=xxx
 *           ← code:202 = 进行中，继续轮询
 *           ← code:200 = 完成，data.oss_image_url 即结果图
 *
 * 全部走 apiClient（带 JWT、走 /web 代理、统一响应拦截器）。
 * 注意：query 接口进行中返回 code:202，必须用 allowBizCodes 放行，
 *       否则会被 apiClient 响应拦截器当作错误 reject 并弹错误框。
 */
import apiClient from './config'
import { uploadFile } from './upload'

var INPAINT_SUBMIT_PATH = '/imageEditor'
var INPAINT_QUERY_PATH = '/queryImageEditorResult'

/* 轮询参数：3 秒间隔，10 分钟超时，与 generationMixin 的 poll 一致 */
var INPAINT_POLL_INTERVAL = 3000
var INPAINT_POLL_TIMEOUT = 10 * 60 * 1000

/**
 * 提交擦除任务：确保 imageUrl / maskUrl 都已上传，再提交到 imageEditor
 * @param {Object} params
 *   - imageUrl  原图 URL（若已上传过，直接复用，避免重复上传）
 *   - imageFile 原图 File（仅当未传 imageUrl 时使用，内部上传）
 *   - maskFile  mask File（前端 Canvas 涂抹导出的黑底白字 PNG，尺寸需与原图一致）
 *               若传入的是 Blob 会被自动包装成 File
 * @returns {Promise<{promptId:string}>}
 */
export function submitInpaint(params) {
  /* 原图：优先用已上传的 imageUrl，否则上传 imageFile */
  var imageJob = params.imageUrl
    ? Promise.resolve(params.imageUrl)
    : uploadFile(params.imageFile)

  return imageJob.then(function(imageUrl) {
    if (!imageUrl) {
      throw new Error('缺少原图，请重试')
    }
    /* mask：File 直接用，Blob 包装成 File */
    var maskFile = params.maskFile
    if (maskFile && !(maskFile instanceof File) && maskFile instanceof Blob) {
      maskFile = new File([maskFile], 'mask.png', { type: 'image/png' })
    }
    return uploadFile(maskFile).then(function(maskUrl) {
      return { imageUrl: imageUrl, maskUrl: maskUrl }
    })
  }).then(function(urls) {
    return apiClient.post(INPAINT_SUBMIT_PATH, {
      imageUrl: urls.imageUrl,
      maskUrl: urls.maskUrl
    }, {
      timeout: 180000
    })
  }).then(function(data) {
    /* apiClient 响应拦截器已解包为业务体 { code, data, message } */
    if (data.code !== 200) {
      throw new Error(data.message || '提交擦除任务失败')
    }
    var promptId = data.data && data.data.prompt_id
    if (!promptId) {
      throw new Error('未拿到任务ID，请重试')
    }
    return { promptId: promptId }
  })
}

/**
 * 查询擦除任务状态（单次）
 * @param {string} promptId
 * @returns {Promise<{code:number, status:string, imageUrl:string, error:string}>}
 *   code: 200 = 完成，202 = 进行中
 *   status: 'running' | 'completed' | 'failed'
 */
export function getInpaintStatus(promptId) {
  return apiClient.get(INPAINT_QUERY_PATH, {
    params: { promptId: promptId },
    /* 放行 202 进行中，避免被响应拦截器当错误 reject + 弹框 */
    allowBizCodes: [202],
    timeout: 30000
  }).then(function(data) {
    if (data.code === 200) {
      return {
        code: 200,
        status: 'completed',
        imageUrl: data.data && data.data.oss_image_url
      }
    }
    if (data.code === 202) {
      return { code: 202, status: 'running' }
    }
    return {
      code: data.code,
      status: 'failed',
      error: data.message || '擦除失败'
    }
  })
}

/**
 * 轮询擦除任务直到完成/失败/超时
 * 用法与 generationMixin 的 pollTask 一致：
 *   pollInpaint(promptId, function(err, imageUrl) { ... })
 *
 * @param {string}   promptId
 * @param {Function} cb    回调 (error, imageUrl)
 * @returns {{stop:Function}} 停止轮询的句柄
 */
export function pollInpaint(promptId, cb) {
  var stopped = false
  var startedAt = Date.now()
  var timer = null

  function tick() {
    if (stopped) return
    if (Date.now() - startedAt > INPAINT_POLL_TIMEOUT) {
      stopped = true
      cb(new Error('擦除任务超时'))
      return
    }

    getInpaintStatus(promptId).then(function(result) {
      if (stopped) return

      if (result.status === 'completed') {
        stopped = true
        cb(null, result.imageUrl)
        return
      }

      if (result.status === 'failed') {
        stopped = true
        cb(new Error(result.error || '擦除失败'))
        return
      }

      /* running → 继续轮询 */
      timer = setTimeout(tick, INPAINT_POLL_INTERVAL)
    }).catch(function(err) {
      if (stopped) return
      stopped = true
      cb(err)
    })
  }

  timer = setTimeout(tick, INPAINT_POLL_INTERVAL)

  return {
    stop: function() {
      stopped = true
      if (timer) clearTimeout(timer)
    }
  }
}
